import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import OtpVerify from "./pages/auth/OTPVerify.js";
import UserDashboard from "./pages/user/UserDashboard";
import PrivateRoute from "./routes/PrivateRoute";
import ProfilePage from "./pages/user/ProfilePage";
import WalletPage from "pages/user/WalletPage";
import CommissionPage from "pages/user/CommissionPage";
import UserStatsPage from "pages/user/UserStatsPage";
import UserSupportPage from "pages/user/UserSupportPage";

import AdminLayout from "./components/admin/layout/AdminLayout";
import DashboardPage from "./pages/admin/DashboardPage";
import UsersPage from "pages/admin/UsersPage";
import TransactionsPage from "pages/admin/TransactionsPage";
import TransactionDetailsPage from "pages/admin/TransactionDetailsPage";
import LoansPage from "pages/admin/LoansPage";
import LoanDetailsPage from "pages/admin/LoanDetailsPage";
import AdminWalletsPage from "pages/admin/AdminWalletsPage";
import WalletDetailsPage from "pages/admin/WalletDetailsPage";
import CommissionsPage from "pages/admin/CommissionsPage";
import StatsPage from "pages/admin/StatsPage";
import SupportPage from "pages/admin/SupportPage";
import NotificationsPage from "pages/admin/NotificationsPage";
import SettingsPage from "pages/admin/SettingsPage";
import ReviewsPage from "pages/admin/ReviewsPage";
import TransactionPage from "pages/user/TransactionPage";
import EmailVerificationPage from "pages/auth/EmailVerificationPage";
import UserDetails from "./pages/admin/UserDetails";
import TransactionHistoryPage from "pages/user/TransactionHistory";
import AppHeader from "components/AppHeader";
import SendRequest from "pages/user/SendRequestPage";
import ScheduleTransfer from "pages/user/ScheduleTransferPage";
import SendEmailsPage from "pages/admin/SendEmailsPage";
import ForgotPassword from "pages/auth/ForgotPassword";
import ResetPassword from "pages/auth/ResetPassword";
import AccountDeletion from "pages/legal/AccountDeletion";

import WalletInterestBanner from "components/common/WalletInterestBannerCard";
import PrivacyPolicy from "pages/legal/PrivacyPolicy";
import TermsOfService from "pages/legal/TermsOfService";
import KYCReviewsPage from "pages/admin/KYCReviewsPage";

function Unauthorized() {
  return (
    <div style={{ padding: 24 }}>
      <h2>Unauthorized</h2>
      <p>You do not have permission to access this page.</p>
    </div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = location.pathname.startsWith("/admin");

  const isPublicRoute =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/otp" ||
    location.pathname === "/verify-email" ||
    location.pathname === "/forgot-password" ||
    location.pathname.startsWith("/reset-password") ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-of-service" ||
    location.pathname === "/account-deletion";

  const showBanner = !isAdmin && !isPublicRoute;

  return (
    <>
      <AppHeader />

      {showBanner && (
        <WalletInterestBanner onClick={() => navigate("/wallet?action=fund")} />
      )}

      <Routes>
        {/* Public auth routes */}
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/otp" element={<OtpVerify />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/account-deletion" element={<AccountDeletion />} />

        {/* ADMIN routes (secured) */}
        <Route
          path="/admin"
          element={
            <PrivateRoute roles={["admin", "superadmin"]}>
              <AdminLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/:id" element={<UserDetails />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="transactions/:id" element={<TransactionDetailsPage />} />
          <Route path="loans" element={<LoansPage />} />
          <Route path="loans/:id" element={<LoanDetailsPage />} />
          <Route path="wallets" element={<AdminWalletsPage />} />
          <Route path="wallets/:id" element={<WalletDetailsPage />} />
          <Route path="commissions" element={<CommissionsPage />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="send-emails" element={<SendEmailsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="kyc" element={<KYCReviewsPage />} />
        </Route>

        {/* USER routes (secured) */}
        <Route
          path="/user"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <UserDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <ProfilePage />
            </PrivateRoute>
          }
        />

        <Route
          path="/history"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <TransactionHistoryPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/wallet"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <WalletPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/transaction"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <TransactionPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/commissions"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <CommissionPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/stats"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <UserStatsPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/support"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <UserSupportPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/send-request"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <SendRequest />
            </PrivateRoute>
          }
        />

        <Route
          path="/schedule-transfer"
          element={
            <PrivateRoute roles={["client", "admin", "superadmin"]}>
              <ScheduleTransfer />
            </PrivateRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </>
  );
}

export default App;
